import { Button, Input, Space, Table } from '@arco-design/web-react'
import { IconDelete, IconPlus } from '@arco-design/web-react/icon'

export interface PayMethodItem {
  name: string
  type: string
  color?: string
}

function parsePayMethods(raw: string): PayMethodItem[] {
  if (!raw?.trim()) return []
  try {
    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed)) return []
    return parsed.map((item) => {
      const o = item as Record<string, unknown>
      return {
        name: String(o.name ?? ''),
        type: String(o.type ?? ''),
        color: o.color != null ? String(o.color) : undefined,
      }
    })
  } catch {
    return []
  }
}

interface Props {
  value: string
  onChange: (json: string) => void
}

/** 可视化编辑 PayMethods / WaffoPayMethods JSON 数组 */
export default function PayMethodsEditor({ value, onChange }: Props) {
  const rows = parsePayMethods(value)

  const update = (next: PayMethodItem[]) => {
    onChange(JSON.stringify(next.filter((r) => r.name.trim() || r.type.trim())))
  }

  const columns = [
    {
      title: '展示名',
      dataIndex: 'name',
      render: (_: unknown, _row: PayMethodItem, index: number) => (
        <Input
          value={rows[index]?.name ?? ''}
          placeholder="支付宝"
          onChange={(v) => {
            const next = [...rows]
            next[index] = { ...next[index], name: v }
            update(next)
          }}
        />
      ),
    },
    {
      title: '类型标识',
      dataIndex: 'type',
      render: (_: unknown, _row: PayMethodItem, index: number) => (
        <Input
          value={rows[index]?.type ?? ''}
          placeholder="alipay"
          onChange={(v) => {
            const next = [...rows]
            next[index] = { ...next[index], type: v }
            update(next)
          }}
        />
      ),
    },
    {
      title: '颜色（可选）',
      dataIndex: 'color',
      width: 140,
      render: (_: unknown, _row: PayMethodItem, index: number) => (
        <Input
          value={rows[index]?.color ?? ''}
          placeholder="#1677ff"
          onChange={(v) => {
            const next = [...rows]
            next[index] = { ...next[index], color: v || undefined }
            update(next)
          }}
        />
      ),
    },
    {
      title: '',
      width: 56,
      render: (_: unknown, _row: PayMethodItem, index: number) => (
        <Button
          type="text"
          status="danger"
          icon={<IconDelete />}
          onClick={() => update(rows.filter((_, i) => i !== index))}
        />
      ),
    },
  ]

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <div className="settings-table-scroll">
        <Table
          columns={columns}
          data={(rows.length ? rows : [{ name: '', type: '' }]).map((r, i) => ({ ...r, _idx: i }))}
          pagination={false}
          rowKey="_idx"
        />
      </div>
      <Button
        type="dashed"
        icon={<IconPlus />}
        onClick={() => update([...rows, { name: '', type: '' }])}
      >
        添加支付方式
      </Button>
    </Space>
  )
}

export function validatePayMethods(raw: string): string | null {
  if (!raw?.trim()) return null
  try {
    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed)) return '必须是 JSON 数组'
    const seen = new Set<string>()
    for (const item of parsed) {
      if (typeof item !== 'object' || item === null || Array.isArray(item)) {
        return '每项须为对象 { name, type }'
      }
      const name = String((item as Record<string, unknown>).name ?? '').trim()
      const type = String((item as Record<string, unknown>).type ?? '').trim()
      if (!name || !type) return '每项须包含非空 name 与 type'
      const key = `${name}\0${type}`
      if (seen.has(key)) return `支付方式重复：${name}`
      seen.add(key)
    }
    return null
  } catch {
    return '不是合法的 JSON 数组'
  }
}
